import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useSignInFlow } from '@mystudioclass/auth';
import { Video, Lock, Eye, EyeOff, User, Mail, Phone, AlertCircle, CheckCircle } from '../components/Icons';
import toast from 'react-hot-toast';

// VITE_API_URL already includes /api, so we use it directly
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

export default function AcceptInvitePage() {
  const { isLoaded, createSignIn } = useSignInFlow();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  // Force light mode for public pages
  useEffect(() => {
    const previousTheme = document.documentElement.getAttribute('data-theme');
    document.documentElement.setAttribute('data-theme', 'light');
    return () => {
      if (previousTheme) {
        document.documentElement.setAttribute('data-theme', previousTheme);
      }
    };
  }, []);

  const [customerData, setCustomerData] = useState(null);
  const [studioData, setStudioData] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Fetch invite data on mount
  useEffect(() => {
    const fetchInviteData = async () => {
      if (!token) {
        setError('No invite token provided. Please use the link from your email.');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/invites/${token}`);
        const result = await response.json();

        if (!response.ok || !result.success) {
          setError(result.message || 'Invalid or expired invite link');
          setLoading(false);
          return;
        }

        setCustomerData(result.data.customer);
        setStudioData(result.data.studio);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching invite:', err);
        setError('Failed to load invite. Please try again.');
        setLoading(false);
      }
    };

    fetchInviteData();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/invites/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        if (result.code === 'ALREADY_ACCEPTED' || result.code === 'ACCOUNT_EXISTS') {
          toast.error(result.message);
          navigate('/login');
          return;
        }
        throw new Error(result.message || 'Failed to create account');
      }

      // Account created - now auto sign in
      toast.success('Account created! Signing you in...');

      try {
        const signInResult = await createSignIn({
          identifier: customerData.email,
          password,
        });

        if (signInResult?.error) {
          // Sign-in failed, fall back to login page
          console.error('Auto sign-in failed:', signInResult.error);
          setSuccess(true);
          setTimeout(() => navigate('/login'), 2000);
          return;
        }

        // Successfully signed in - redirect to dashboard
        toast.success('Welcome!');
        window.location.href = '/dashboard';
      } catch (signInErr) {
        console.error('Auto sign-in error:', signInErr);
        // Fall back to login page
        setSuccess(true);
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (err) {
      console.error('Accept invite error:', err);
      toast.error(err.message || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: 'var(--bg-base)' }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4" style={{ color: 'var(--text-secondary)' }}>Loading your invite...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 py-12"
        style={{ background: 'var(--bg-base)' }}
      >
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-xl mb-4"
              style={{ background: 'var(--error-10, rgba(239, 68, 68, 0.1))' }}
            >
              <AlertCircle className="w-8 h-8" style={{ color: 'var(--error, #ef4444)' }} />
            </div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Invalid Invite</h1>
            <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>{error}</p>
          </div>

          <div
            className="rounded-2xl shadow-xl p-8 text-center"
            style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-muted)'
            }}
          >
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              If you believe this is a mistake, please contact your studio to request a new invite.
            </p>
            <Link
              to="/login"
              className="btn btn-primary"
            >
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 py-12"
        style={{ background: 'var(--bg-base)' }}
      >
        <div className="max-w-md w-full text-center">
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-xl mb-4"
            style={{ background: 'var(--success-10, rgba(34, 197, 94, 0.1))' }}
          >
            <CheckCircle className="w-8 h-8" style={{ color: 'var(--success, #22c55e)' }} />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Account Created!</h1>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            Signing you in...
          </p>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ background: 'var(--bg-base)' }}
    >
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-xl mb-4"
            style={{
              background: 'linear-gradient(135deg, var(--primary-hover), var(--primary))',
              boxShadow: '0 0 40px var(--primary-30)'
            }}
          >
            <Video className="w-8 h-8" style={{ color: 'var(--bg-base)' }} />
          </div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>Welcome!</h1>
          <p style={{ color: 'var(--text-muted)' }} className="mt-2">
            You've been invited to join <strong style={{ color: 'var(--text-primary)' }}>{studioData?.name}</strong>
          </p>
        </div>

        {/* Form */}
        <div
          className="rounded-2xl shadow-xl p-8"
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-muted)'
          }}
        >
          <div
            className="flex items-center justify-center mb-6 p-3 rounded-lg"
            style={{ background: 'var(--bg-muted)' }}
          >
            <User className="w-5 h-5 mr-2" style={{ color: 'var(--primary)' }} />
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Set Up Your Account</span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Pre-filled customer info (read-only) */}
            <div className="space-y-4 pb-4 border-b" style={{ borderColor: 'var(--border-muted)' }}>
              <div>
                <label className="label">
                  <User className="w-4 h-4 inline mr-1" />
                  Name
                </label>
                <input
                  type="text"
                  value={`${customerData?.firstName} ${customerData?.lastName}`}
                  className="input bg-opacity-50"
                  style={{ background: 'var(--bg-muted)' }}
                  disabled
                />
              </div>

              <div>
                <label className="label">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email
                </label>
                <input
                  type="email"
                  value={customerData?.email}
                  className="input bg-opacity-50"
                  style={{ background: 'var(--bg-muted)' }}
                  disabled
                />
              </div>

              {customerData?.telephone && (
                <div>
                  <label className="label">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={customerData.telephone}
                    className="input bg-opacity-50"
                    style={{ background: 'var(--bg-muted)' }}
                    disabled
                  />
                </div>
              )}
            </div>

            {/* Password fields */}
            <div>
              <label className="label">
                <Lock className="w-4 h-4 inline mr-1" />
                Create Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pr-10"
                  placeholder="At least 8 characters"
                  minLength={8}
                  required
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: 'var(--text-muted)' }}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="label">
                <Lock className="w-4 h-4 inline mr-1" />
                Confirm Password
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input"
                placeholder="Confirm your password"
                minLength={8}
                required
                disabled={submitting}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn btn-primary w-full"
            >
              {submitting ? 'Creating Account...' : 'Create My Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-medium transition-colors"
                style={{ color: 'var(--primary)' }}
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
